import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { convertToMergeAwareTables } from 'restaurantAdmin/floorPlans/utils';
import {
  RESERVATIONS_CONCIERGE_PATH,
  RESERVATIONS_OCCUPANTS_PAGE_PATH,
  RESERVATIONS_SERVERS_PAGE_PATH,
  RESERVATIONS_WAITLIST_PAGE_PATH,
} from 'restaurantAdmin/paths';
import { Button, ButtonVariants } from '@components/button/Button';
import { LoadingPage } from '@components/LoadingPage';
import { LinkStyledAsButton } from '@components/linkStyledAsButton/LinkStyledAsButton';
import {
  DndContext,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useHandleClickOutside } from '@shared/hooks/useHandleClickOutside';
import { MOBILE } from '@shared/styles/breakpoints';
import cx from 'classnames';
import { add } from 'date-fns';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import typography from '~styles/typography.scss';
import { DatePicker } from '../../components/datePicker/DatePicker';
import { PageContent } from '../../layout/PageContent';
import { PageHeader } from '../../layout/PageHeader';
import { SplitMergeFloorPlan } from '../splitMergeTables/SplitMergeFloorPlan';
import { SplitMergeTablesSheet } from '../splitMergeTables/SplitMergeTablesSheet';
import { isReservation, type ServiceReservation } from './apiHelpers';
import { BlockOrUnblockReservationsButton } from './BlockOrUnblockReservationsButton';
import {
  DATE_PARAM,
  DRAWER_PARAM,
  SEAT_MODE_PARAM,
  SEAT_WALK_IN_FORM,
} from '../../utils/constants';
import { DraggingAvatar } from './DraggingAvatar';
import { OccupantFloorPlan } from './floorPlan/OccupantFloorPlan';
import { ReservationSeatModeFloorPlan } from './floorPlan/ReservationSeatModeFloorPlan';
import { WalkInSeatModeFloorPlan } from './floorPlan/WalkInSeatModeFloorPlan';
import { useMultiSelectOccupants } from './hooks/useMultiSelectOccupants';
import { useServiceSearchParams } from './hooks/useServiceSearchParams';
import { PageActions } from './PageActions';
import { ServiceDrawer } from './ServiceDrawer';
import { ServiceFloorPlanContainer } from './ServiceFloorPlanContainer';
import { ServiceOverviewButton } from './ServiceOverviewButton';
import styles from './ServicePage.scss';
import { ServicePageKebabMenu } from './ServicePageKebabMenu';
import { StatsButton } from './StatsButton';
import type { WaitListEntry } from './sidePanel/waitList/apiHelpers';
import { useWaitListContext } from './sidePanel/waitList/state/WaitListContext';
import {
  SidePanelSheetMode,
  useReservationServiceContext,
} from './state/ReservationServiceContext';
import { useReservationServiceSelectedSeatingContext } from './state/ReservationServiceSelectedSeatingContext';
import { toISODateFormat } from '@shared/utils/dateFormatters';

export const NAVIGATE_TO_SERVICE_SEARCH_PARAMS_KEY = 'navigateToService';

export const ServicePage = () => {
  const location = useLocation();
  const restaurant = useRestaurant();
  const isTabletOrMobile = useMediaQuery({ maxWidth: MOBILE });

  const [draggingReservation, setDraggingReservation] =
    useState<ServiceReservation>();
  const [draggingWaitListEntry, setDraggingWaitListEntry] =
    useState<WaitListEntry>();
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      tolerance: 10,
    },
  });
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const {
    exitSeatMode,
    enterSeatMode,
    openSeatWalkInFormDrawer,
    searchParams,
  } = useServiceSearchParams();

  const {
    timersByFloorPlanTableId,
    selectedFloorPlan,
    hasInitialDataLoaded,
    selectedDate,
    setSelectedDate,
    selectedOccupant,
    setSidePanelSheet,
    sidePanelSheet,
  } = useReservationServiceContext();
  const { setSelectedFloorPlanTables } =
    useReservationServiceSelectedSeatingContext();
  const { setSelectedWaitListEntryIndex } = useWaitListContext();
  const {
    isMultiSelectingOccupants,
    toggleMultiSelectingOccupants,
    multiSelectionOccupantCount,
    messageSelectedOccupants,
  } = useMultiSelectOccupants();

  const seatModeEnabled = searchParams.has(SEAT_MODE_PARAM);

  // custom hooks
  const { ref: floorPlanRef } = useHandleClickOutside((event) => {
    // prevent the exiting of seat mode when the click is housed within a modal
    if ((event.target as HTMLElement)?.closest('[role=dialog]')) return;

    exitSeatMode();
  });

  // handlers
  const onSeatWalkIn = (floorPlanTable: HostFloorPlanTable) => {
    openSeatWalkInFormDrawer();
    setSelectedFloorPlanTables([floorPlanTable]);
  };

  if (!hasInitialDataLoaded) return <LoadingPage />;

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    enterSeatMode();
    if (active.data.current?.reservation) {
      setDraggingReservation(active.data.current?.reservation);
    }
    if (active.data.current?.waitListEntry) {
      setDraggingWaitListEntry(active.data.current?.waitListEntry);
      setSelectedWaitListEntryIndex(active.data.current?.waitListEntryIndex);
    }
  };

  const resetDragAndDropState = () => {
    exitSeatMode();
    setDraggingReservation(undefined);
    setDraggingWaitListEntry(undefined);
  };

  const buildBookReservationUrl = () => {
    const params: Record<string, string> = {
      [NAVIGATE_TO_SERVICE_SEARCH_PARAMS_KEY]: 'true',
    };
    if (searchParams.has(DATE_PARAM)) {
      params[DATE_PARAM] = toISODateFormat(selectedDate);
    }
    return `${RESERVATIONS_CONCIERGE_PATH}?${new URLSearchParams(params)}`;
  };

  const getHighlightedListingIds = () => {
    if (selectedOccupant && isReservation(selectedOccupant)) {
      return selectedOccupant.tableAssignments?.map(
        (tableAssignment) => tableAssignment.listingId,
      );
    }

    if (draggingReservation) {
      return draggingReservation.tableAssignments?.map(
        (tableAssignment) => tableAssignment.listingId,
      );
    }

    return undefined;
  };

  // Avoid destroying the Tab component to maintain scroll position
  const isTabsVisible = sidePanelSheet.mode === SidePanelSheetMode.None;

  const renderSidePanelSheet = () => {
    if (sidePanelSheet.mode === SidePanelSheetMode.SplitMerge) {
      return (
        <SplitMergeTablesSheet
          className={styles.sidePanelSheet}
          handleOnCloseSheet={() => {
            setSidePanelSheet({ mode: SidePanelSheetMode.None });
          }}
        />
      );
    }

    return null;
  };

  const renderFloorPlan = () => {
    if (!selectedFloorPlan) {
      return (
        <ServiceFloorPlanContainer
          instructions="No floor plan available for this date"
          testId="no-floor-plan"
        />
      );
    }

    const mergeAwareFloorPlanTables = convertToMergeAwareTables(
      selectedFloorPlan.floorPlanTables,
    );
    if (sidePanelSheet.mode === SidePanelSheetMode.SplitMerge)
      return (
        <ServiceFloorPlanContainer>
          <SplitMergeFloorPlan
            tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
            backgroundSrc={selectedFloorPlan.backgroundSrc}
            className={styles.floorPlan}
            tables={selectedFloorPlan.floorPlanTables}
          />
        </ServiceFloorPlanContainer>
      );

    if (!seatModeEnabled) {
      const selectedWalkInTableId = searchParams.get(
        'selectedTableId',
      ) as string;

      const listingIdsToPulsate = isReservation(selectedOccupant)
        ? selectedOccupant.tableAssignments?.map(
            (tableAssignment) => tableAssignment.listingId,
          )
        : undefined;
      return (
        <ServiceFloorPlanContainer testId="seated-floor-plan">
          <OccupantFloorPlan
            backgroundSrc={selectedFloorPlan.backgroundSrc}
            className={styles.ocuppantFloorPlan}
            emptyTableOnClick={onSeatWalkIn}
            listingIdsToPulsate={listingIdsToPulsate}
            selectedEmptyTableId={selectedWalkInTableId}
            tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
            tables={mergeAwareFloorPlanTables}
          />
        </ServiceFloorPlanContainer>
      );
    }

    // seat mode is enabled under this line
    const isWaitListTabActive = location.pathname.endsWith(
      RESERVATIONS_WAITLIST_PAGE_PATH,
    );

    switch (true) {
      case !!draggingReservation:
        return (
          <ServiceFloorPlanContainer
            instructions="Drop on a highlighted table to seat them."
            ref={floorPlanRef}
            testId="seated-floor-plan"
          >
            <ReservationSeatModeFloorPlan
              backgroundSrc={selectedFloorPlan?.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              highlightedListingIds={getHighlightedListingIds()}
              tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
              tables={mergeAwareFloorPlanTables}
            />
          </ServiceFloorPlanContainer>
        );
      case isReservation(selectedOccupant):
        return (
          <ServiceFloorPlanContainer
            instructions="Click a highlighted table to seat them."
            ref={floorPlanRef}
            testId="seated-floor-plan"
          >
            <ReservationSeatModeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              highlightedListingIds={getHighlightedListingIds()}
              tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
              tables={mergeAwareFloorPlanTables}
            />
          </ServiceFloorPlanContainer>
        );
      case searchParams.get(DRAWER_PARAM) === SEAT_WALK_IN_FORM:
        return (
          <ServiceFloorPlanContainer
            instructions="Select one or more tables to seat the walk-in."
            ref={floorPlanRef}
            testId="seated-floor-plan"
          >
            <WalkInSeatModeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
              tables={mergeAwareFloorPlanTables}
            />
          </ServiceFloorPlanContainer>
        );
      case isWaitListTabActive && !!draggingWaitListEntry:
        return (
          <ServiceFloorPlanContainer
            instructions="Drop on an empty table to seat them."
            ref={floorPlanRef}
            testId="seated-floor-plan"
          >
            <WalkInSeatModeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
              tables={mergeAwareFloorPlanTables}
            />
          </ServiceFloorPlanContainer>
        );
      case isWaitListTabActive:
        return (
          <ServiceFloorPlanContainer
            instructions="Select one or more tables"
            ref={floorPlanRef}
          >
            <WalkInSeatModeFloorPlan
              backgroundSrc={selectedFloorPlan.backgroundSrc}
              className={cx(styles.floorPlan, styles.floorPlanSeatMode)}
              tableTimersByFloorPlanTableId={timersByFloorPlanTableId}
              tables={mergeAwareFloorPlanTables}
            />
          </ServiceFloorPlanContainer>
        );
      default:
        return null;
    }
  };

  const tabClassNames = ({ isActive }: { isActive: boolean }) =>
    cx({
      [styles.tab]: true,
      [styles.activeTab]: isActive,
      [typography.t1]: true,
    });

  const pageHeader = () => {
    if (isMultiSelectingOccupants) {
      const restaurantCannotSendTextMessage = !restaurant.smsPhoneNumber;

      return (
        <PageHeader title="Select Multiple Guests">
          <Button
            onClick={messageSelectedOccupants}
            label="Text Message"
            isDisabled={
              multiSelectionOccupantCount === 0 ||
              restaurantCannotSendTextMessage
            }
            variant={ButtonVariants.Secondary}
          />
          <Button
            onClick={toggleMultiSelectingOccupants}
            label="Cancel Selection"
            variant={ButtonVariants.Tertiary}
          />
        </PageHeader>
      );
    }

    return (
      <PageHeader
        customNode={
          <DatePicker
            handleOnChangeCalendarDate={(date) => {
              setSelectedDate(date);
            }}
            handleOnClickNextDay={() => {
              setSelectedDate(add(selectedDate, { days: 1 }));
            }}
            handleOnClickPrevDay={() => {
              setSelectedDate(add(selectedDate, { days: -1 }));
            }}
            selectedDate={selectedDate}
          />
        }
      >
        <div className={styles.pageActions}>
          <StatsButton />
          <ServiceOverviewButton />
          <BlockOrUnblockReservationsButton />
          <LinkStyledAsButton
            label={isTabletOrMobile ? 'Book' : 'Book Reservation'}
            to={buildBookReservationUrl()}
            variant={ButtonVariants.Secondary}
            className={styles.bookReservationButton}
          />
          <ServicePageKebabMenu />
        </div>
      </PageHeader>
    );
  };

  return (
    <DndContext
      onDragCancel={resetDragAndDropState}
      onDragEnd={resetDragAndDropState}
      onDragStart={handleDragStart}
      sensors={sensors}
      modifiers={[snapCenterToCursor]}
    >
      <DraggingAvatar />
      {pageHeader()}

      <PageContent
        className={cx('requires-unconstrained-layout', styles.content)}
      >
        <div className={styles.sidePanel} data-testid="side-panel">
          <div
            className={styles.tabContent}
            data-testid="tabs"
            style={{ display: isTabsVisible ? undefined : 'none' }}
          >
            {!isMultiSelectingOccupants && (
              <div className={styles.tabBar}>
                <NavLink
                  className={tabClassNames}
                  to={RESERVATIONS_OCCUPANTS_PAGE_PATH}
                >
                  Occupants
                </NavLink>
                <NavLink
                  className={({ isActive }) =>
                    cx(tabClassNames({ isActive }), styles.waitlistTab)
                  }
                  to={RESERVATIONS_WAITLIST_PAGE_PATH}
                >
                  Waitlist
                </NavLink>
                <NavLink
                  className={({ isActive }) =>
                    cx(tabClassNames({ isActive }), styles.serversTab)
                  }
                  to={RESERVATIONS_SERVERS_PAGE_PATH}
                >
                  Servers
                </NavLink>
              </div>
            )}

            <Outlet />
          </div>
          {renderSidePanelSheet()}
        </div>

        <div className={styles.seatingContent}>
          <PageActions />
          {renderFloorPlan()}
        </div>
        <ServiceDrawer allowActivationOfSeatMode={true} />
      </PageContent>
    </DndContext>
  );
};
